import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Timeline,
  Title,
} from "@mantine/core";
import { IconCircleCheck, IconMessage2, IconX } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router";
import {
  CLIENT_REQUEST_TYPE_LABELS,
  SAMPLE_PACK_LABELS,
  type ClientClarificationDto,
  type ClientRequestDetailDto,
  type FileRefDto,
} from "@mexp/shared";
import { http } from "@/api/client";
import { ErrorState, LoadingState } from "@/components/States";
import { FileList } from "@/features/files/FileList";
import { FileUploader } from "@/features/files/FileUploader";
import { ReasonModal } from "@/features/requests/components/ReasonModal";
import { notifyError, notifySuccess } from "@/lib/notify";
import { portalKeys, useClientRequest, useClientSample } from "./api";
import { BackButton } from "./BackButton";
import { ReleaseFiles } from "./ReleaseFiles";
import { DocumentChecklist, RequestStatus } from "./RequestStatus";
import { SampleCard } from "./SampleTracker";
import { fullDate } from "./stage";

/** A history detail that reads as a fact beside the date ("by Asha", "via DHL · tracking …") rather than a quote. */
const isFact = (detail: string | null) => !!detail && /^(by|via) /.test(detail);

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
      <Text fz="sm" c="dimmed" style={{ flex: "none" }}>
        {label}
      </Text>
      <Text fz="sm" ta="right" style={{ minWidth: 0 }}>
        {children || "—"}
      </Text>
    </Group>
  );
}

/**
 * The dossier Makin released, to download, with the button to raise a query on it. Dossiers from before queries were
 * answered with compliance can have earlier versions, each with what changed in it.
 */
function Releases({
  r,
  onRequestChanges,
}: {
  r: ClientRequestDetailDto;
  onRequestChanges: () => void;
}) {
  const [latest, ...earlier] = r.releases;
  if (!latest) return null;
  return (
    <Stack gap="lg">
      <Card
        p={{ base: "md", sm: "xl" }}
        style={{
          background: "var(--mantine-color-teal-light)",
          borderColor: "var(--mantine-color-teal-outline)",
        }}
      >
        <Stack gap="md">
          <Group
            justify="space-between"
            align="flex-start"
            wrap="wrap"
            gap="sm"
          >
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon size={40} radius="xl" color="teal" variant="filled">
                <IconCircleCheck size={24} />
              </ThemeIcon>
              <Stack gap={0}>
                <Title order={3} fz={20}>
                  {latest.version > 1
                    ? `Version ${latest.version} is ready`
                    : "Your documents are ready"}
                </Title>
                <Text fz="sm" c="var(--mantine-color-teal-light-color)">
                  Approved by Makin on{" "}
                  {fullDate(latest.releasedAt.slice(0, 10))}.
                </Text>
              </Stack>
            </Group>
            {r.canRequestChanges && (
              <Button
                variant="default"
                leftSection={<IconMessage2 size={16} />}
                onClick={onRequestChanges}
              >
                Request Query
              </Button>
            )}
          </Group>
          {latest.changeNote && (
            <Text fz="sm" c="var(--mantine-color-teal-light-color)">
              Includes the changes you asked for: “{latest.changeNote}”
            </Text>
          )}
          <ReleaseFiles release={latest} />
        </Stack>
      </Card>

      {earlier.length > 0 && (
        <Card>
          <Stack gap="md">
            <Title order={4}>Earlier versions</Title>
            {earlier.map((v, i) => (
              <Fragment key={v.version}>
                {i > 0 && <Divider />}
                <Stack gap={6}>
                  <Group gap="xs">
                    <Text fz="sm" fw={600}>
                      Version {v.version}
                    </Text>
                    <Text fz="xs" c="dimmed" className="tabular">
                      approved {fullDate(v.releasedAt.slice(0, 10))}
                    </Text>
                  </Group>
                  {v.changeNote && (
                    <Text fz="sm" c="dimmed">
                      Changed because you asked: “{v.changeNote}”
                    </Text>
                  )}
                  <FileList files={v.files} />
                  {v.link && (
                    <Anchor
                      href={v.link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      fz="sm"
                    >
                      {v.link.label || "Open the shared folder"}
                    </Anchor>
                  )}
                </Stack>
              </Fragment>
            ))}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

/** The client's queries on the dossier, newest first, each with Makin's compliance once it is sent. */
function Queries({ r }: { r: ClientRequestDetailDto }) {
  if (!r.changeRequests.length) return null;
  const open = r.changeRequests.filter((q) => q.open).length;
  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between" align="baseline">
          <Title order={4}>Your queries</Title>
          <Text fz="sm" c="dimmed">
            {open ? `${open} open` : "All answered"}
          </Text>
        </Group>
        {r.changeRequests.map((q, i) => (
          <Fragment key={q.id}>
            {i > 0 && <Divider />}
            <Stack gap={8}>
              <Group gap="xs" justify="space-between">
                <Group gap="xs">
                  <Text fz="sm" fw={600}>
                    Query {q.number}
                  </Text>
                  <Badge
                    size="sm"
                    variant="light"
                    color={q.open ? "grape" : "teal"}
                  >
                    {q.open ? "Open" : "Answered"}
                  </Badge>
                </Group>
                <Text fz="xs" c="dimmed" className="tabular">
                  {q.requestedBy} · {fullDate(q.requestedAt.slice(0, 10))}
                </Text>
              </Group>
              <Text fz="sm" style={{ whiteSpace: "pre-wrap" }}>
                {q.comment}
              </Text>
              {q.documents.length > 0 && (
                <Group gap={6}>
                  {q.documents.map((d) => (
                    <Badge key={d} size="sm" variant="light" color="gray" tt="none">
                      {d}
                    </Badge>
                  ))}
                </Group>
              )}
              {q.files.length > 0 && <FileList files={q.files} />}
              {q.compliance ? (
                <Box
                  p="sm"
                  style={{
                    background: "var(--mantine-color-teal-light)",
                    borderRadius: "var(--mantine-radius-md)",
                  }}
                >
                  <Stack gap={6}>
                    <Text
                      fz="xs"
                      fw={600}
                      c="var(--mantine-color-teal-light-color)"
                    >
                      Makin's compliance ·{" "}
                      {fullDate(q.compliance.sentAt.slice(0, 10))}
                    </Text>
                    <Text fz="sm" style={{ whiteSpace: "pre-wrap" }}>
                      {q.compliance.comment}
                    </Text>
                    {q.compliance.files.length > 0 && (
                      <FileList files={q.compliance.files} />
                    )}
                  </Stack>
                </Box>
              ) : (
                q.resolvedVersion != null && (
                  <Text fz="sm" c="dimmed">
                    Answered by version {q.resolvedVersion}.
                  </Text>
                )
              )}
            </Stack>
          </Fragment>
        ))}
      </Stack>
    </Card>
  );
}

/** One of Makin's clarifications: what they need, and the client's answer (or the form to send it). */
function ClarificationEntry({
  r,
  c,
  onAnswered,
}: {
  r: ClientRequestDetailDto;
  c: ClientClarificationDto;
  onAnswered: (r: ClientRequestDetailDto) => void;
}) {
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState<FileRefDto[]>([]);
  const [busy, setBusy] = useState(false);
  const send = useMutation({
    mutationFn: () =>
      http.post<ClientRequestDetailDto>(
        `/portal/requests/${r.id}/clarifications/${c.id}/answer`,
        { comment: comment.trim(), fileIds: files.map((f) => f.id) },
      ),
    onSuccess: (res) => {
      onAnswered(res);
      notifySuccess("Your answer was sent to Makin.");
    },
    onError: (e) => notifyError(e),
  });
  return (
    <Stack gap={8}>
      <Group gap="xs" justify="space-between">
        <Group gap="xs">
          <Text fz="sm" fw={600}>
            Clarification {c.number}
          </Text>
          <Badge size="sm" variant="light" color={c.open ? "orange" : "teal"}>
            {c.open ? "Waiting for your answer" : "Answered"}
          </Badge>
          <Text fz="xs" c="dimmed">
            {c.query ? `About Query ${c.query}` : "About your requisition"}
          </Text>
        </Group>
        <Text fz="xs" c="dimmed" className="tabular">
          {fullDate(c.askedAt.slice(0, 10))}
        </Text>
      </Group>
      <Text fz="sm" style={{ whiteSpace: "pre-wrap" }}>
        {c.question}
      </Text>
      {c.files.length > 0 && <FileList files={c.files} />}
      {c.answer ? (
        <Box
          p="sm"
          style={{
            background: "var(--mantine-color-teal-light)",
            borderRadius: "var(--mantine-radius-md)",
          }}
        >
          <Stack gap={6}>
            <Text fz="xs" fw={600} c="var(--mantine-color-teal-light-color)">
              Answered by {c.answer.answeredBy} ·{" "}
              {fullDate(c.answer.answeredAt.slice(0, 10))}
            </Text>
            <Text fz="sm" style={{ whiteSpace: "pre-wrap" }}>
              {c.answer.comment}
            </Text>
            {c.answer.files.length > 0 && <FileList files={c.answer.files} />}
          </Stack>
        </Box>
      ) : (
        r.canAnswerClarifications && (
          <Stack gap="sm">
            <Textarea
              label="Your answer"
              withAsterisk
              autosize
              minRows={2}
              maxRows={10}
              value={comment}
              onChange={(e) => setComment(e.currentTarget.value)}
            />
            <FileUploader
              area="dr_reference"
              value={files}
              onChange={setFiles}
              onBusyChange={setBusy}
              label="Reference files (optional)"
            />
            <Group justify="flex-end">
              <Button
                disabled={!comment.trim() || busy}
                loading={send.isPending}
                onClick={() => send.mutate()}
              >
                {busy ? "Waiting for uploads…" : "Send answer"}
              </Button>
            </Group>
          </Stack>
        )
      )}
    </Stack>
  );
}

/**
 * Makin's clarifications, newest first: when the requirement is not clear, Makin asks the client to explain their
 * requisition (or a query) or to send reference documents before the work starts. Open ones take the answer here.
 */
function Clarifications({
  r,
  onAnswered,
}: {
  r: ClientRequestDetailDto;
  onAnswered: (r: ClientRequestDetailDto) => void;
}) {
  if (!r.clarifications.length) return null;
  const open = r.clarifications.filter((c) => c.open).length;
  return (
    <Card
      style={
        open ? { borderColor: "var(--mantine-color-orange-outline)" } : undefined
      }
    >
      <Stack gap="md">
        <Group justify="space-between" align="baseline">
          <Title order={4}>
            {open ? "Makin needs more information" : "Clarifications from Makin"}
          </Title>
          <Text fz="sm" c="dimmed">
            {open ? `${open} waiting for your answer` : "All answered"}
          </Text>
        </Group>
        {r.clarifications.map((c, i) => (
          <Fragment key={c.id}>
            {i > 0 && <Divider />}
            <ClarificationEntry r={r} c={c} onAnswered={onAnswered} />
          </Fragment>
        ))}
      </Stack>
    </Card>
  );
}

/** The client raises a query on the released dossier; Makin answers it with a compliance. */
function RequestChangesModal({
  r,
  opened,
  onClose,
  onDone,
}: {
  r: ClientRequestDetailDto;
  opened: boolean;
  onClose: () => void;
  onDone: (r: ClientRequestDetailDto) => void;
}) {
  const [comment, setComment] = useState("");
  const [documents, setDocuments] = useState<string[]>([]);
  const [files, setFiles] = useState<FileRefDto[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (opened) {
      setComment("");
      setDocuments([]);
      setFiles([]);
    }
  }, [opened]);
  // Queries are numbered in the order they are raised.
  const next = r.changeRequests.length + 1;
  const send = useMutation({
    mutationFn: () =>
      http.post<ClientRequestDetailDto>(`/portal/requests/${r.id}/changes`, {
        comment: comment.trim(),
        documents,
        fileIds: files.map((f) => f.id),
      }),
    onSuccess: (res) => {
      onDone(res);
      notifySuccess(
        `Query ${next} sent to Makin. Their compliance will appear on this page.`,
      );
    },
    onError: (e) => notifyError(e),
  });
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Query ${next}`}
      size="lg"
    >
      <Stack>
        {/* <Text fz="sm" c="dimmed">
          Makin's regulatory team answers it with a compliance: a reply and any
          corrected documents. Your dossier stays available to download
          meanwhile.
        </Text> */}
        <Textarea
          label="Query details?"
          placeholder="For example: the authority wants the COA on our letterhead"
          withAsterisk
          autosize
          minRows={3}
          maxRows={10}
          value={comment}
          onChange={(e) => setComment(e.currentTarget.value)}
        />
        {r.documents.length > 0 && (
          <Stack gap={6}>
            <Text fz="sm" fw={500}>
              Documents it is about{" "}
              <Text span c="dimmed" fz="sm" fw={400}>
                (optional)
              </Text>
            </Text>
            <Chip.Group multiple value={documents} onChange={setDocuments}>
              <Group gap={8}>
                {r.documents.map((d) => (
                  <Chip
                    key={d.key}
                    value={d.key}
                    variant="outline"
                    radius="sm"
                    size="sm"
                  >
                    {d.name}
                  </Chip>
                ))}
              </Group>
            </Chip.Group>
          </Stack>
        )}
        <FileUploader
          area="dr_reference"
          value={files}
          onChange={setFiles}
          onBusyChange={setBusy}
          label="Files that explain the query (optional)"
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!comment.trim() || busy}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            {busy ? "Waiting for uploads…" : "Send query"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function ClientRequestPage() {
  const { id = "" } = useParams();
  const query = useClientRequest(id);
  const sample = useClientSample(id);
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<"cancel" | "files" | "changes" | null>(
    null,
  );
  const [files, setFiles] = useState<FileRefDto[]>([]);
  const [busy, setBusy] = useState(false);

  const refresh = (r: ClientRequestDetailDto) => {
    queryClient.setQueryData(portalKeys.request(r.id), r);
    void queryClient.invalidateQueries({ queryKey: portalKeys.all });
  };
  const cancel = useMutation({
    mutationFn: (reason: string) =>
      http.post<ClientRequestDetailDto>(`/portal/requests/${id}/cancel`, {
        reason,
      }),
    onSuccess: (r) => {
      refresh(r);
      setModal(null);
      notifySuccess(`${r.code} withdrawn.`);
    },
    onError: (e) => notifyError(e),
  });
  const addFiles = useMutation({
    mutationFn: () =>
      http.post<ClientRequestDetailDto>(
        `/portal/requests/${id}/reference-files`,
        { fileIds: files.map((f) => f.id) },
      ),
    onSuccess: (r) => {
      refresh(r);
      setModal(null);
      setFiles([]);
      notifySuccess("Files added to your dossier.");
    },
    onError: (e) => notifyError(e),
  });

  if (query.isLoading || query.isError || !query.data) {
    return (
      <Stack gap="md">
        <BackButton fallback="/requests" />
        {query.isLoading ? (
          <LoadingState />
        ) : (
          <ErrorState
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        )}
      </Stack>
    );
  }
  const r = query.data;

  return (
    <Stack gap="md">
      <Stack gap="md">
        <BackButton fallback="/requests" />
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Stack gap={6} style={{ minWidth: 0 }}>
            <Title
              order={1}
              fz={{ base: 24, sm: 30 }}
              style={{ letterSpacing: "-0.015em", textWrap: "balance" }}
            >
              {r.productName}{" "}
              <Text span c="dimmed" fw={400} inherit>
                for {r.country}
              </Text>
            </Title>
            <Text fz="sm" c="dimmed" className="tabular">
              {r.code} · raised by {r.raisedBy} on{" "}
              {fullDate(r.createdAt.slice(0, 10))}
            </Text>
          </Stack>
          {r.canCancel && (
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconX size={16} />}
              onClick={() => setModal("cancel")}
            >
              Withdraw requisition
            </Button>
          )}
        </Group>
      </Stack>

      <RequestStatus r={r} />

      <Clarifications r={r} onAnswered={refresh} />

      <Releases r={r} onRequestChanges={() => setModal("changes")} />

      <Queries r={r} />

      {/* A withdrawn requisition's sample stops where it was; one already sent stays followable. */}
      {sample.data &&
        (r.stage !== "cancelled" ||
          sample.data.status === "dispatched" ||
          sample.data.status === "delivered") && (
          <SampleCard sample={sample.data} />
        )}

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Stack gap="lg">
            <DocumentChecklist r={r} />

            <Card>
              <Stack gap="md">
                <Title order={4}>History</Title>
                <Timeline
                  bulletSize={14}
                  lineWidth={2}
                  active={r.timeline.length - 1}
                >
                  {r.timeline.map((e, i) => (
                    <Timeline.Item key={`${e.at}-${i}`} title={e.label}>
                      <Stack gap={6}>
                        <Text fz="xs" c="dimmed" className="tabular">
                          {new Date(e.at).toLocaleString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                          {/* "by …" and "via …" stay on the date line; a comment or reason reads as a quote. */}
                          {isFact(e.detail) ? ` · ${e.detail}` : ""}
                        </Text>
                        {e.detail && !isFact(e.detail) && (
                          <Text fz="sm" style={{ whiteSpace: "pre-wrap" }}>
                            “{e.detail}”
                          </Text>
                        )}
                        {!!e.documents?.length && (
                          <Group gap={4}>
                            <Text fz="xs" c="dimmed">
                              Documents:
                            </Text>
                            {e.documents.map((d) => (
                              <Badge
                                key={d}
                                size="sm"
                                variant="light"
                                color="gray"
                                tt="none"
                              >
                                {d}
                              </Badge>
                            ))}
                          </Group>
                        )}
                        {!!e.files?.length && <FileList files={e.files} />}
                      </Stack>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="lg">
            <Card>
              <Stack gap="sm">
                <Title order={4}>Details</Title>
                <Fact label="You asked for">
                  {r.requestType
                    ? CLIENT_REQUEST_TYPE_LABELS[r.requestType]
                    : null}
                </Fact>
                {r.genericName ? (
                  <>
                    <Fact label="Generic name">{r.genericName}</Fact>
                    <Fact label="Brand name">{r.brandName}</Fact>
                  </>
                ) : (
                  <Fact label="Brand / Generic name">{r.productName}</Fact>
                )}
                <Fact label="Destination">{r.country}</Fact>
                <Fact label="Category">{r.category}</Fact>
                <Fact label="Dosage form">{r.dosageForm}</Fact>
                <Fact label="Pack size">{r.packSize}</Fact>
                {r.similarProduct && <Fact label="Existing similar product">{r.similarProduct}</Fact>}
                <Fact label="Sample">
                  {sample.data
                    ? SAMPLE_PACK_LABELS[sample.data.packType]
                    : null}
                </Fact>
                <Fact label="Target completion date">
                  {r.requiredBy
                    ? fullDate(r.requiredBy)
                    : "Date to be confirmed by Makin"}
                </Fact>
                {r.notes && (
                  <Box pt={4}>
                    <Text fz="sm" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
                      {r.notes}
                    </Text>
                  </Box>
                )}
              </Stack>
            </Card>
            <Card>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Title order={4}>Your reference files</Title>
                  {r.canAddFiles && (
                    <Button
                      size="compact-sm"
                      variant="subtle"
                      onClick={() => setModal("files")}
                    >
                      Add files
                    </Button>
                  )}
                </Group>
                <FileList files={r.referenceFiles} empty="None attached" />
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>

      <ReasonModal
        opened={modal === "cancel"}
        onClose={() => setModal(null)}
        title={`Withdraw ${r.code}`}
        description="Makin stops working on this dossier. This cannot be undone; send a new requisition if you need it again."
        label="Why are you withdrawing it?"
        confirmLabel="Withdraw requisition"
        confirmColor="red"
        loading={cancel.isPending}
        onSubmit={(res) => cancel.mutate(res.reason)}
      />
      <RequestChangesModal
        r={r}
        opened={modal === "changes"}
        onClose={() => setModal(null)}
        onDone={(res) => {
          refresh(res);
          setModal(null);
        }}
      />
      <Modal
        opened={modal === "files"}
        onClose={() => setModal(null)}
        title="Add reference files"
        size="lg"
      >
        <Stack>
          <FileUploader
            area="dr_reference"
            value={files}
            onChange={setFiles}
            onBusyChange={setBusy}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModal(null)}>
              Cancel
            </Button>
            <Button
              disabled={!files.length || busy}
              loading={addFiles.isPending}
              onClick={() => addFiles.mutate()}
            >
              Add to dossier
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
